import {
  utc,
  UTC,
  guid,
  TimeSpan,
}                                 from 'rewire-common';
import { observable, freeze }     from 'rewire-core';
import { ITemplate, getMultiple } from '../DataTypes';
import mainDateRangeNavModel      from '../ui/MainDateRangeNavModel';
import {
  formatTimeNoRollover,
  localTimeZoneUTCNow,
}                                 from '../utility/DateUtils';

export interface IGeneratorCalendarOptions {
  calendarStartDate?:    UTC;
  calendarEndDate?:      UTC;
  calendarDisplayDate?:  UTC;
  offset?:               number;
  repeat?:               boolean;
  templateCalendarData?: ITemplateCalendarEvent[];
  locked?:               boolean;
}

export interface ITemplateCalendarEvent {
  id:          string;
  templateId?: string;
  title?:      string;
  tooltip?:    string;
  start:       UTC;
  end:         UTC;
  allDay?:     boolean;
  resource?:   any;
  color?:      string;
  locked?:     boolean;
}

interface IGeneratorCalendarModelState {
  locked:                         boolean;
  changed:                        boolean;
  calendarStartDate?:             UTC;
  calendarEndDate?:               UTC;
  calendarDisplayDate?:           UTC;
  repeat:                         boolean;
  originalTemplateCalendarEvents: ITemplateCalendarEvent[];
  templateCalendarEvents:         ITemplateCalendarEvent[];
  selectedTemplateCalendarEvent?: ITemplateCalendarEvent;
}

class GeneratorCalendarModel {
  state: IGeneratorCalendarModelState;

  constructor() {
    this.state = observable({
      changed:                        false,
      locked:                         false,
      calendarStartDate:              undefined,
      calendarEndDate:                undefined,
      calendarDisplayDate:            undefined,
      repeat:                         false,
      templateCalendarEvents:         [],
      originalTemplateCalendarEvents: [],
      selectedTemplateCalendarEvent:  undefined,
    });
  }

  reset() {
    freeze(() => {
      this.state.changed                        = false;
      this.state.locked                         = false;
      this.state.calendarStartDate              = undefined;
      this.state.calendarEndDate                = undefined;
      this.state.calendarDisplayDate            = undefined;
      this.state.repeat                         = false;
      this.state.templateCalendarEvents         = [];
      this.state.originalTemplateCalendarEvents = [];
      this.state.selectedTemplateCalendarEvent  = undefined;
    });
  }

  load(options?: IGeneratorCalendarOptions) {
    const opts = options || {};
    freeze(() => {
      this.state.changed                       = false;
      this.state.locked                        = !!opts.locked;
      this.state.calendarStartDate             = opts.calendarStartDate?.clone();
      this.state.calendarEndDate               = opts.calendarEndDate?.clone();
      this.state.calendarDisplayDate           = opts.calendarDisplayDate?.clone();
      this.state.repeat                        = !!opts.repeat;
      this.state.selectedTemplateCalendarEvent = undefined;
    });

    freeze(() => {
      if (opts.templateCalendarData) {
        this.loadCalendarEvents(opts.templateCalendarData, opts.offset);
      } else {
        this.state.templateCalendarEvents         = [];
        this.state.originalTemplateCalendarEvents = [];
      }
    });
  }

  loadCalendarEvents(events: ITemplateCalendarEvent[], offset?: number) {
    const calendarEvents = events.slice();
    if (calendarEvents.length && offset) {
      while (offset < 0) {
        offset += calendarEvents.length;
      }
      for (let i = 0; i < offset % calendarEvents.length; i++) {
        calendarEvents.push(calendarEvents.shift()!);
      }
    }

    const newCalendarEvents: ITemplateCalendarEvent[] = [];
    for (let i = 0; i < calendarEvents.length; i++) {
      const calendarEventDate = this.calendarStartDate.add(i);
      const calendarEvent     = calendarEvents[i];
      const newTemplateCalendarEvent: ITemplateCalendarEvent = {
        id :        guid(),
        start:      calendarEventDate,
        end:        calendarEventDate.clone(),
        templateId: calendarEvent?.templateId,
        tooltip:    calendarEvent?.tooltip,
        title:      calendarEvent?.title,
        allDay:     true,
        color:      calendarEvent?.color,
      };

      newCalendarEvents.push(newTemplateCalendarEvent);
    }
    this.state.templateCalendarEvents         = newCalendarEvents;
    this.state.originalTemplateCalendarEvents = newCalendarEvents.slice();
  }

  get locked(): boolean {
    return this.state.locked;
  }

  get changed(): boolean {
    return this.state.changed;
  }

  get calendarStartDate(): UTC {
    return this.state.calendarStartDate ?? mainDateRangeNavModel.startDateStartOfDay;
  }

  get calendarEndDate(): UTC | undefined {
    return this.state.calendarEndDate;
  }

  get calendarDisplayDate(): UTC {
    return this.state.calendarDisplayDate ?? mainDateRangeNavModel.startDateStartOfDay;
  }

  get repeat(): boolean {
    return this.state.repeat;
  }

  get originalTemplateCalendarEvents(): ITemplateCalendarEvent[] {
    return this.state.originalTemplateCalendarEvents;
  }

  get templateCalendarEvents(): ITemplateCalendarEvent[] {
    return this.state.templateCalendarEvents;
  }

  get validTemplateCalendarEvents(): ITemplateCalendarEvent[] {
    return this.templateCalendarEvents.filter((event: ITemplateCalendarEvent) => !!(event.start >= this.calendarStartDate && (!this.calendarEndDate || event.end <= this.calendarEndDate)) );
  }

  get displayedTemplateCalendarEvents(): ITemplateCalendarEvent[] {
    const startOfCurrMonth = this.calendarDisplayDate.clone().startOfMonth();
    const endOfCurrMonth   = this.calendarDisplayDate.clone().endOfMonth();

    if (!this.repeat) {
      return this.templateCalendarEvents.filter((event: ITemplateCalendarEvent) => event.start >= this.calendarStartDate && event.end <= endOfCurrMonth);
    } else if (!this.validTemplateCalendarEvents.length || endOfCurrMonth < this.calendarStartDate || (this.calendarEndDate && startOfCurrMonth > this.calendarEndDate)) {
      return [];
    }

    const events         = this.validTemplateCalendarEvents.filter((event: ITemplateCalendarEvent) => event.start >= startOfCurrMonth && event.end <= endOfCurrMonth);
    const latestEventDay = events.length ? events.reduce((prev: ITemplateCalendarEvent, curr: ITemplateCalendarEvent) => prev.end > curr.end ? prev : curr, events[0]).end.day : 0;

    for (let i = latestEventDay + 1; i <= this.calendarDisplayDate.daysInMonth; i++) {
      const calendarEventDate = startOfCurrMonth.add(i - 1);
      if (this.calendarEndDate && calendarEventDate > this.calendarEndDate) {
        break;
      }

      const daysSinceCalendarStart = Math.abs(this.calendarStartDate.subtractDate(calendarEventDate));
      const repeatedEvent          = {...this.validTemplateCalendarEvents[daysSinceCalendarStart % this.validTemplateCalendarEvents.length]};
      repeatedEvent.id           = guid();
      repeatedEvent.start        = calendarEventDate;
      repeatedEvent.end          = calendarEventDate.clone();
      events.push(repeatedEvent);
    }

    return events;
  }

  get selectedTemplateCalendarEvent(): ITemplateCalendarEvent | undefined {
    return this.state.selectedTemplateCalendarEvent;
  }

  getTemplateCalendarEvents(templates: (ITemplate | undefined)[]): ITemplateCalendarEvent[] {
    return templates.map((template: ITemplate | undefined) => {
      return this.getTemplateCalendarEvent(template);
    });
  }

  getTemplateCalendarEvent(template?: ITemplate): ITemplateCalendarEvent {
    let tooltip = '';
    if (template) {
      let parts = '';
      if (template.template) {
        const start     = Math.min(...template.template.map(t => t.start!));
        const end       = Math.max(...template.template.map(t => t.end!));
        const units     = template.template.reduce((sum, t) => sum + t.units!, 0);
        const hrs       = (units === 1) ? 'hr' : 'hrs';
        const startTime = isNaN(start) ? '' : localTimeZoneUTCNow().startOfDay().add(start, TimeSpan.hours, 2);
        const endTime   = isNaN(end)   ? '' : localTimeZoneUTCNow().startOfDay().add(end,   TimeSpan.hours, 2);
        const reason    = template.template.reduce((r, t) => {
          if ((r === '') || (t.reason?.name === '')) return t.reason?.name;
          if (r === t.reason?.name) return r;
          return getMultiple();
        }, '');
        parts = (isNaN(start) || isNaN(end))
                ? `${units}${hrs} ${reason}`
                : `${formatTimeNoRollover(utc(startTime))}-${formatTimeNoRollover(utc(endTime))} ${units}${hrs} ${reason}`;
      }
      tooltip = `${template.code || template.name} ${parts}`;
    }
    return {
      id:         guid(),
      templateId: template?.id,
      title:      template?.code || template?.name,
      tooltip:    tooltip,
      start:      undefined as any, // can this really be undefined???
      end:        undefined as any,
      allDay:     true,
      color:      template?.fields?.['_color:t'],
      locked:     template?.locked,
    };
  }

  dateIsWithinCurrentMonth(date: UTC): boolean {
    return date.year === this.calendarDisplayDate.year && date.month === this.calendarDisplayDate.month;
  }

  isValidCalendarDate(date: UTC): boolean {
    return !!(date >= this.calendarStartDate && (!this.calendarEndDate || date <= this.calendarEndDate));
  }

  canDrag(event: ITemplateCalendarEvent) {
    return !this.locked && this.isValidCalendarDate(event.start) && this.dateIsWithinCurrentMonth(event.start);
  }

  hasChanges(): boolean {
    const eventTemplateIds         = this.templateCalendarEvents.map(event => event.templateId);
    const originalEventTemplateIds = this.originalTemplateCalendarEvents.map(event => event.templateId);
    if (eventTemplateIds.length !== originalEventTemplateIds.length) {
      return true;
    }
    for (let i = 0; i < eventTemplateIds.length; i++) {
      if (eventTemplateIds[i] !== originalEventTemplateIds[i]) {
        return true;
      }
    }

    return false;
  }

  setCalendarStartDate(newCalendarStartDate?: UTC) {
    this.state.calendarStartDate = newCalendarStartDate?.clone();

    freeze(() => {
      this.templateCalendarEvents.forEach((event: ITemplateCalendarEvent, idx: number) => {
        const newDate = this.calendarStartDate.add(idx);
        event.start = newDate;
        event.end   = newDate.clone();
      });
    });
  }

  setCalendarEndDate(newCalendarEndDate?: UTC) {
    this.state.calendarEndDate = newCalendarEndDate?.clone();
  }

  setCalendarDisplayDate(newCalendarDisplayDate?: UTC) {
    this.state.calendarDisplayDate = newCalendarDisplayDate?.clone();
  }

  setRepeat(value: boolean) {
    this.state.repeat = value;
  }

  setSelectedEvent(event?: ITemplateCalendarEvent) {
    this.state.selectedTemplateCalendarEvent = event;
  }

  removeEvent(event: ITemplateCalendarEvent) {
    if (this.locked) {
      return;
    }

    const eventIdx = this.state.templateCalendarEvents.findIndex((evt: ITemplateCalendarEvent) => evt === event);
    if (eventIdx >= 0) {
      const dayOffEvent: ITemplateCalendarEvent = {
        id:         guid(),
        templateId: undefined,
        title:      undefined,
        start:      event.start,
        end:        event.end,
        allDay:     true,
        color:      undefined,
      };
      this.state.templateCalendarEvents.splice(eventIdx, 1, dayOffEvent);
    }

    this.state.changed = this.hasChanges();
  }

  updateEvents = (event: ITemplateCalendarEvent, start: UTC, end: UTC) => {
    if (this.locked || !this.isValidCalendarDate(start) || !this.dateIsWithinCurrentMonth(start)) {
      return;
    }

    // move or add event
    const idx                        = this.templateCalendarEvents.findIndex((evt: ITemplateCalendarEvent) => evt.id === event.id);
    const existingEventOnGivenDayIdx = this.templateCalendarEvents.findIndex((evt: ITemplateCalendarEvent) => evt.start.toDateString() === start.toDateString() && evt.end.toDateString() === end.toDateString());

    if (idx >= 0) {
      // Move event on calendar. Move this event to given date, and move any event already there to the previous date of the moved event (swap).
      if (existingEventOnGivenDayIdx >= 0) {
        const existingEventOnGivenDay                             = this.templateCalendarEvents[existingEventOnGivenDayIdx];
        existingEventOnGivenDay.start                           = event.start;
        existingEventOnGivenDay.end                             = event.end;
        event.start                                             = start;
        event.end                                               = end;
        this.templateCalendarEvents[idx]                        = existingEventOnGivenDay;
        this.templateCalendarEvents[existingEventOnGivenDayIdx] = event;
        this.state.changed                                      = this.hasChanges();
        return;
      }
      event.start = start;
      event.end   = end;
      this.sortEvents();
    } else {
      // Add new event to calendar, and move any event already there off of the calendar.
      const newEvent   = {...event};
      newEvent.id    = guid();
      newEvent.start = start;
      newEvent.end   = end;
      if (existingEventOnGivenDayIdx >= 0) {
        this.templateCalendarEvents.splice(existingEventOnGivenDayIdx, 1, newEvent);
      } else {
        this.templateCalendarEvents.push(newEvent);
        this.sortEvents();
      }
    }

    this.state.changed = this.hasChanges();
  };

  eventPositionCompare(a: ITemplateCalendarEvent, b: ITemplateCalendarEvent): number {
    return a.start < b.start ? -1 : a.start > b.start ? 1 : 0;
  }

  sortEvents() {
    this.templateCalendarEvents.sort(this.eventPositionCompare);
  }
}

export default GeneratorCalendarModel;
